import React from 'react';
import { useNavigate } from 'react-router-dom';

const ForOwners = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      title: 'Guaranteed Rent on Time',
      description: 'We guarantee rent payments on time, every month. Your income is protected regardless of tenant occupancy or payment delays.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      title: 'Full Property Responsibility',
      description: 'We take complete responsibility for your flat on the tenant\'s behalf—handling all maintenance, repairs, and property care.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      title: 'Time Freedom',
      description: 'Spend your time on what matters most—we handle all the day-to-day property management tasks.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
      title: 'Value Growth',
      description: 'Strategic improvements and market insights help increase your property\'s long-term value.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      ),
      title: '24/7 Support',
      description: 'Round-the-clock assistance for emergencies, questions, and property-related concerns.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      title: 'Transparent Reporting',
      description: 'Monthly statements and financial reports keep you informed about your property\'s performance.'
    }
  ];

  return (
    <section className="bg-white py-16 md:py-24 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-furniflex-teal/10 text-furniflex-teal rounded-full text-sm font-semibold uppercase tracking-wide mb-4">
            FOR OWNERS
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
            Designed Specifically
            <span className="block text-furniflex-teal">For Property Owners</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get guaranteed rent on time, complete property management, and peace of mind. We handle everything while you enjoy your monthly income.
          </p>
        </div>

        {/* Key Features Grid - 2x3 Layout */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Our Core Services
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {/* Professional Management */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-furniflex-teal hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-furniflex-teal rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-900 uppercase">Professional Management</h4>
            </div>

            {/* Tenant Screening */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-furniflex-teal hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-furniflex-teal rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-900 uppercase">Tenant Screening</h4>
            </div>

            {/* Guaranteed Rent */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-furniflex-teal hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-furniflex-teal rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <line x1="6" y1="8" x2="18" y2="8" />
                  <line x1="6" y1="12" x2="18" y2="12" />
                  <line x1="6" y1="16" x2="12" y2="16" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-900 uppercase">Guaranteed Rent</h4>
            </div>

            {/* Regular Inspections */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-furniflex-teal hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-furniflex-teal rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                  <path d="M11 8h0M14 11h-3M11 14v2" />
                  <circle cx="7" cy="7" r="1" fill="white" />
                  <circle cx="15" cy="7" r="1" fill="white" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-900 uppercase">Regular Inspections</h4>
            </div>

            {/* Value Enhancement */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-furniflex-teal hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-furniflex-teal rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="16 12 12 8 8 12" />
                  <polyline points="8 12 12 16 16 12" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-900 uppercase">Value Enhancement</h4>
            </div>

            {/* Timely Maintenance */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-furniflex-teal hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-furniflex-teal rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-900 uppercase">Timely Maintenance</h4>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Property Owners Choose FURNIFLEX
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-furniflex-teal rounded-xl flex items-center justify-center text-white mb-6">
                  {benefit.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h4>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gray-900 rounded-2xl p-12 text-white">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Partner With Us?
          </h3>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join hundreds of property owners who have simplified their investment journey with FURNIFLEX.
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="px-8 py-4 bg-furniflex-teal text-white rounded-lg font-semibold text-lg hover:bg-teal-600 transition-colors shadow-lg"
          >
            Get Started Today
          </button>
        </div>
      </div>
    </section>
  );
};

export default ForOwners;

